import { formatNumber, NgTemplateOutlet, AsyncPipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, viewChild } from '@angular/core';
import { FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { BehaviorSubject, combineLatest, distinctUntilChanged, filter, map, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';

import { setInputFocus } from 'apps/cmg-clear2-ui/src/app/shared/utils/focus.util';
import { SessionStore } from '../../../core/state';
import {
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003EmploymentIncomeExpensesStore,
  Cmg1003FileDataQuery,
  Cmg1003LoanQuery,
  Cmg1003LoanStore,
  Cmg1003StoreQueryService,
} from '../../../loan-state';
import { calculateSum, canUseUnsavedChanges, nonNullish } from '../../../shared/utils';
import {
  CombinedHousingExpenseModel,
  EmploymentIncomeExpenseModel,
  HousingExpenseModel,
  PropertyTaxUpdateType,
} from '../../../cmg-1003/models/employment-income-expense.model';
import { LoanDataModel, LoanPurposeType, MortgageType, SubordinateFinanceModel } from '../../../cmg-1003/models/loan-information.model';
import { otherExpenseTypes, PrepaidItemType, subFiTypes } from '../../../cmg-1003/models/lookups/expenses.lookups';
import {
  calculateHCLTV,
  calculatePI,
  calculateProposedPropertyTax,
  calculateProposedPropertyTaxFactor,
  createNewOtherExpense,
  createOtherExpenseFormGroup,
  findVisibleOtherExpenses,
} from './housing-expenses.util';
import { ConfirmationModalCloseType, SyncType } from '../../../models';
import { ConfirmationModalComponent } from '../../../core';
import { DialogService } from 'primeng/dynamicdialog';
import { Panel } from 'primeng/panel';
import { TableModule } from 'primeng/table';
import { CmgPrimeNgInputNumberFocusDirective } from '../../directives/cmg-input-number-focus.directive';
import { CmgDecimalInputComponent } from '../../../../../../../libs/ui/src/lib/components/cmg-decimal-input/cmg-decimal-input.component';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { Button, ButtonDirective } from 'primeng/button';
import { CmgValidatorMessagesComponent } from '../cmg-validator-messages/cmg-validator-messages.component';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { CmgFormControlDomDirective } from '../../directives/cmg-form-control-dom.directive';
import { CmgFocusSelectDirective } from '../../directives/cmg-focus-select.directive';
import { QaSlugPipe } from '../../pipes/qa-slug.pipe';

@Component({
  selector: 'cmg-housing-expenses',
  templateUrl: './housing-expenses.component.html',
  styleUrls: ['./housing-expenses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Panel,
    PrimeTemplate,
    ReactiveFormsModule,
    TableModule,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    CmgDecimalInputComponent,
    NgTemplateOutlet,
    InputGroup,
    InputGroupAddon,
    Button,
    CmgValidatorMessagesComponent,
    Select,
    Checkbox,
    ButtonDirective,
    Dialog,
    CmgFormControlDomDirective,
    CmgFocusSelectDirective,
    AsyncPipe,
    DecimalPipe,
    QaSlugPipe,
  ],
})
export class HousingExpensesComponent implements OnInit, OnDestroy {
  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _empIncExpStore = inject(Cmg1003EmploymentIncomeExpensesStore);
  private readonly _loanQuery = inject(Cmg1003LoanQuery);
  private readonly _loanStore = inject(Cmg1003LoanStore);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _messageService = inject(MessageService);
  private readonly _dialogService = inject(DialogService);

  readonly monthlyPaymentInput = viewChild<InputNumber>('monthlyPaymentInput');
  readonly subFiTypeInput = viewChild<Select>('subFiTypeInput');

  employmentIncomeExpense$ = this._empIncExpQuery.selectActiveEmploymentIncomeExpense();
  subordinateFinancing$ = this._loanQuery.select().pipe(map((loan) => loan.subOrdinateFinance));

  /**
   * The combined rent of application's borrower(s), based on Borrower store.
   *
   * [Note]: Can't use `employmentIncomeExpense$.presentRent`, since it becomes out-of-date after user updates rent in "Borrower" section.
   */
  presentRent$ = this._storeQuery.getBorrowersBySelectedApplicationId().pipe(
    filter((x) => x?.length > 0),
    distinctUntilChanged(),
    map((borrowers) => borrowers.map((b) => b.presentAddress?.monthlyRent ?? 0).reduce((a, b) => a + b)),
  );

  /**
   * The form representing 'combinedHousingExpenses' in EmploymentIncomeExpenseModel.
   * The form's value is used for the employmentIncomeExpenses store.
   */
  expensesForm = this._fb.group(
    {
      isIncludedInPiti: false,
      presentRent: this._fb.control({ value: undefined, disabled: true }), // Rent
      firstMortgage: this._createExpenseFormGroup({ proposedAmountDisabled: true }), // First Mortgage (P & I)
      otherMortgage: this._createExpenseFormGroup({ proposedAmountDisabled: true }), // Subordinate Lien(s) (P & I)
      hazardInsurance: this._createExpenseFormGroup(), // Homeowner's Insurance
      propertyInsurance: this._createExpenseFormGroup({ proposedAmountDisabled: true }), // Supplemental Property Insurance
      realEstateTaxes: this._createExpenseFormGroup(), // Property Taxes
      mortgageInsurance: this._createExpenseFormGroup({ factorDisabled: true, proposedAmountDisabled: true }), // Mortgage Insurance
      homeOwnerAssnDues: this._createExpenseFormGroup(), // Association / Project Dues
      othersTotalPropertyInsurance: this._createExpenseFormGroup(), // Others (To Include Supplemental Property Insurance)
      otherHousingExpenses: this._fb.array([]), // 'Select Other Expense' dropdown(s)
      othersTotal: this._createExpenseFormGroup({ presentAmountDisabled: true, proposedAmountDisabled: true }), // Others Total
      total: this._createExpenseFormGroup(), // Total
      customUiProperty: true,
      originalHousingExpenses: undefined,
    },
    { updateOn: 'change' },
  );

  rebuildExpenseDropdownOptions$ = new BehaviorSubject<void>(null);
  /**
   * The selectable dropdown options for the dynamic "Other Expense" row(s).
   *
   * [Mechanism]: Each dropdown option can only be selected once.
   * Once that option is selected, it's disabled, which the CSS uses to hide the option. */
  otherExpenseDropdownOptions$ = combineLatest([
    this.otherHousingExpenses.valueChanges.pipe(startWith(null)),
    this.rebuildExpenseDropdownOptions$,
  ]).pipe(
    map(() => {
      const visibleOtherExpenses = this.otherHousingExpenses.controls.map((c) => c.value.expenseType as PrepaidItemType);
      return otherExpenseTypes.map((option) => {
        return { ...option, optionDisabled: visibleOtherExpenses.includes(option.id) && option.value !== PrepaidItemType.NotAssigned };
      });
    }),
  );

  /** The form representing the calculator modal. Not saved to store nor Byte. */
  calculatorModalForm = this._fb.group({
    monthlyPayment: undefined,
    yearlyPayment: undefined,
  });
  calculatorFormSnapshot = this.calculatorModalForm.value;

  /** Determines whether to display calculator modal (PrimeNG Dialog). */
  showCalculatorModal = false;

  /** The column name that opened the calculator modal. */
  calculatorColumnName = null;

  /** This form should use and update data for LoanDataModel.subOrdinateFinance.  */
  subFiModalForm = this._fb.group({
    subFiType: [],
    subFiBaseLoan: [],
    subFiIntRate: [],
    subFiTerm: [],
    calculatedPi: [{ value: null, disabled: true }],
    subFiIntOnly: [],
    helocMaxBalance: [],
    hcltv: [{ value: null, disabled: true }],
    excludeSubFi: [],
  });

  /** Determines whether to display Subordinate Financing modal (PrimeNG Dialog). */
  showSubFiModal = false;

  /** Hard-coded lookups for Expenses UI. */
  subFiTypes = subFiTypes;

  currentApplicationId: number;

  isFullyDisabled$ = this._storeQuery.statusRequiresDisabling$();

  autoRecovery = {
    /** Represents whether user clicked "Unsaved Changes" in auto-recovery modal. */
    clickedUnsavedChanges: false,

    /** Represents whether Expenses is currentkly showing unsaved data from auto-recovery. */
    isShowingUnsavedExpenses: false,
  };

  /** View model to use in the HTML template via async pipe */
  vm$ = combineLatest({
    otherExpenseDropdownOptions: this.otherExpenseDropdownOptions$,
    isFullyDisabled: this.isFullyDisabled$,
    subordinateFinancing: this.subordinateFinancing$,
    loan: this._loanQuery.selectLoanInfo(),
  });

  private _syncType = SyncType.None;
  private _destroyed$ = new Subject<boolean>();

  get otherHousingExpenses() {
    return this.expensesForm.get('otherHousingExpenses') as UntypedFormArray;
  }

  ngOnInit() {
    this._fileDataQuery
      .select((f) => f)
      .pipe(takeUntil(this._destroyed$))
      .subscribe((f) => {
        this._syncType = f.syncType;
      });

    this.employmentIncomeExpense$
      .pipe(
        filter((x) => !!x?.applicationId),
        distinctUntilChanged(),
        takeUntil(this._destroyed$),
        tap((empIncExp) => {
          // Represents whether a save was just performed, based on a proxy:
          // After a save, the expenses data in the store will have NO custom UI properties.
          const justPerformedSave = !(empIncExp?.combinedHousingExpenses as any).customUiProperty;

          // Represents whether user switched applications.
          const switchedApplications = this.currentApplicationId !== empIncExp.applicationId;
          this.currentApplicationId = empIncExp.applicationId;

          this.autoRecovery.clickedUnsavedChanges = canUseUnsavedChanges(this._sessionStore.getValue(), this._empIncExpQuery.dirtyChecker2.isDirty());

          const forAutoRecovery = this.autoRecovery.clickedUnsavedChanges && !this.autoRecovery.isShowingUnsavedExpenses;

          // Regenerate this.expensesForm after:
          // 1) Switching applications,
          // 2) After performing save,
          // 3) OR after selecting "Unsaved Changes" on auto-recovery modal.
          if (switchedApplications || justPerformedSave || forAutoRecovery) {
            // [Scenario]: New application
            if (empIncExp.applicationId < 0) {
              this._empIncExpQuery
                .selectAll()
                .pipe(
                  take(1),
                  tap((entities: EmploymentIncomeExpenseModel[]) => {
                    // All applications should share the same expense data, so a new application also uses this same data.
                    const expensesForNewApplication: CombinedHousingExpenseModel = entities.filter((e) => e.applicationId > 0)[0]
                      ?.combinedHousingExpenses;

                    this._initializeExpensesForm(expensesForNewApplication);
                    this._assembleControlsForOtherExpenses(expensesForNewApplication?.otherHousingExpenses, true);
                  }),
                )
                .subscribe();
            }

            // [Scenario]: Existing application
            else {
              this._initializeExpensesForm(empIncExp?.combinedHousingExpenses);
              this._assembleControlsForOtherExpenses(empIncExp?.combinedHousingExpenses?.otherHousingExpenses, true, forAutoRecovery);
            }

            if (this.autoRecovery.clickedUnsavedChanges) {
              this.autoRecovery.isShowingUnsavedExpenses = true;
            }
          }

          // Clear 1 disables input fields only.
          // It doesn’t disable add/delete expenses buttons, the calculator modal button, and the Subordinate Finance modal link.
          this._storeQuery
            .statusRequiresDisabling$()
            .pipe(
              take(1),
              tap((isFullyDisabled) => {
                if (isFullyDisabled) {
                  this.expensesForm.disable({ emitEvent: false });
                }
              }),
            )
            .subscribe();
        }),
      )
      .subscribe();

    this._subscribeToLoanInfoChanges();

    this.expensesForm.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      this._processExpensesData();
    });

    this._subscribeToCalculatorModalFormChanges();
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  /** Adds a blank row for dynamic "Other Expense" section. */
  addOtherExpenseRow() {
    const newFormGroup = createOtherExpenseFormGroup(this._fb);
    newFormGroup.patchValue(createNewOtherExpense(), { emitEvent: false });
    newFormGroup.controls.proposedAmount.disable({ emitEvent: false });

    this.otherHousingExpenses.push(newFormGroup);

    // Now update store, since form is fully updated and ready.
    this._processExpensesData();
  }

  /** From Clear 1: subordinate-finance-calc-modal.component.ts - calculateHcltv(). */
  calculateHcltv(loan: LoanDataModel) {
    // Constants are from Clear 1 code: employment-income-expenses.page.ts - showSubordinateFinanceModal().
    const mortgageType = loan.mortgageAppliedFor;

    const helocCreditAmount = parseInt(this.subFiModalForm.get('helocMaxBalance').value);

    // Original Clear 1 logic for baseLoanAmount, from employment-income-expenses.page.ts:
    // (this.employmentInfo.mortgageType == 4 || this.employmentInfo.mortgageType == 3) ? noteLoanAmount : this.employmentInfo.subOrdinateFinance.loanAmount;
    // noteLoanAmount and subOrdinateFinance.loanAmount were both derived from totalLoanAmount.
    const baseLoanAmount = loan.totalLoanAmount;

    const appraisedValue =
      loan.loanPurpose === LoanPurposeType.refinance || (!!loan.mortgageAppliedFor && loan.mortgageAppliedFor === MortgageType.usda)
        ? loan.appraisalValue
        : loan.appraisalValue !== 0 && loan.appraisalValue < loan.salesPrice
          ? loan.appraisalValue
          : loan.salesPrice;

    // Logic is from Clear 1 code: subordinate-finance-calc-modal.component.ts - calculateHcltv().
    if (helocCreditAmount >= 0 && !!baseLoanAmount && !!appraisedValue) {
      const hcltvValue = calculateHCLTV(helocCreditAmount, baseLoanAmount, appraisedValue);

      this.subFiModalForm
        .get('hcltv')
        .patchValue(mortgageType === MortgageType.conventional ? Math.ceil(hcltvValue) : formatNumber(hcltvValue, 'en-US'), { emitEvent: false });
    }
  }

  /** Calculates the "Calculated P & I" field in "Subordinate Financing" modal. */
  calculateSubFiPI() {
    this.subFiModalForm
      .get('calculatedPi')
      .setValue(
        calculatePI(
          this.subFiModalForm.get('subFiBaseLoan').value,
          this.subFiModalForm.get('subFiIntRate').value,
          this.subFiModalForm.get('subFiTerm').value,
          this.subFiModalForm.get('subFiIntOnly').value,
        ),
        { emitEvent: false },
      );

    // To mimic Clear 1 behavior: User cleared out "Loan Amount" --> Replace with 0.
    if (!this.subFiModalForm.get('subFiBaseLoan')?.value) {
      this.subFiModalForm.get('subFiBaseLoan').patchValue(0, { emitEvent: false });
    }
  }

  /** Closes calculator modal, and resets its form. */
  closeCalculatorModal() {
    this.calculatorModalForm.reset();
    this.calculatorColumnName = null;
    this.showCalculatorModal = false;
  }

  /** Closes "Subordinate Financing" modal, and resets its form. */
  closeSubFiModal() {
    this.subFiModalForm.reset();
    this.showSubFiModal = false;
  }

  /**
   * Deleting EXISTING expense (i.e. has PrepaidItemId) = Setting its proposedAmount to null.
   *
   * Deleting NEW expense (i.e PrepaidItemId is null) = Remove from this.otherHousingExpenses FormArray.
   */
  deleteOtherExpenseRow(index: number) {
    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      draggable: true,
      keepInViewport: true,
      data: {
        message: 'Are you sure you want to delete this?',
        header: 'Confirm',
        icon: 'pi pi-trash',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
      },
    });

    dialogRef.onClose.subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        this.otherHousingExpenses.removeAt(index);

        // Now update store, since form is fully updated and ready.
        this._processExpensesData();
      }
    });
  }

  /**
   * Opens calculator modal, initialized with the monthly amount.
   * Will auto-calculate yearly amount if monthly amount was changed, and vice versa.
   * @param initialMonthlyAmount
   * @param columnName
   */
  openCalculatorModal(initialMonthlyAmount: number, columnName: 'presentAmount' | 'proposedAmount') {
    this.showCalculatorModal = true;
    this.calculatorColumnName = columnName;

    // Initialize calculator modal form.
    this.calculatorModalForm.patchValue(
      {
        monthlyPayment: initialMonthlyAmount ?? 0,
        yearlyPayment: nonNullish(initialMonthlyAmount) ? initialMonthlyAmount * 12 : 0,
      },
      { emitEvent: false },
    );

    // Get a snapshot of the form's values.
    // This is used to determine whether monthly or yearly payment was updated.
    this.calculatorFormSnapshot = this.calculatorModalForm.value;

    setInputFocus(this.monthlyPaymentInput());
  }

  /** Opens "Subordinate Financing" modal, initialized with latest data from this.subordinateFinancing$. */
  openSubFiModal(isFullyDisabled: boolean, subFi: SubordinateFinanceModel) {
    if (isFullyDisabled) {
      return;
    }

    this.showSubFiModal = true;
    this.subFiModalForm.patchValue(subFi, { emitEvent: false });
    setInputFocus(this.subFiTypeInput());
  }

  /** Applies the monthly amount from calculator modal to this.expensesForm. */
  syncCalculatorModalToExpensesForm() {
    this.expensesForm
      .get('hazardInsurance.' + this.calculatorColumnName)
      .patchValue(this.calculatorModalForm.get('monthlyPayment').value, { emitEvent: false });
    this.closeCalculatorModal();
  }

  /** Updates the Loan store's "subordinateFinance" with the values from the "Subordinate Financing" modal form. */
  syncSubFiModalToLoanStore(subFi: SubordinateFinanceModel) {
    this._loanStore.update({ subOrdinateFinance: { ...subFi, ...this.subFiModalForm.getRawValue() } });
    this.closeSubFiModal();
  }

  onInputBlur(controlId: any, formControl?: FormControl): void {
    if (controlId === 'real-estate-taxes-factor') {
      this._updatePropertyTaxes(PropertyTaxUpdateType.Factor, formControl?.value);
    }
    if (controlId === 'real-estate-taxes-proposed-amount') {
      this._updatePropertyTaxes(PropertyTaxUpdateType.Amount, formControl?.value);
    }
  }

  private _createExpenseFormGroup(state?: { presentAmountDisabled?: boolean; factorDisabled?: boolean; proposedAmountDisabled?: boolean }) {
    return this._fb.group({
      presentAmount: this._fb.control({ value: undefined, disabled: state?.presentAmountDisabled }),
      factor: this._fb.control({ value: undefined, disabled: state?.factorDisabled }),
      expenseType: undefined,
      proposedAmount: this._fb.control({ value: undefined, disabled: state?.proposedAmountDisabled }),
      prepaidItemId: undefined,
      premiumPaidToType: undefined,
    });
  }

  /**
   * Assembles FormGroups for each visible "Other Expense".
   * @param otherExpenses The "Other Expenses" data - e.g. "Supplemental Expense (1)".
   * @param isNgOnInit Default: `false`. Indicates whether this was called within `ngOnInit`.
   * @param forAutoRecovery Default: `false`. If `true`, this function will use the `otherExpenses` param as-is. (Mainly for auto-recovery)
   */
  private _assembleControlsForOtherExpenses(otherExpenses: HousingExpenseModel[], isNgOnInit = false, forAutoRecovery = false) {
    const visibleOtherExpenses = forAutoRecovery ? otherExpenses : findVisibleOtherExpenses(otherExpenses);

    // Sort "Other Expenses" by expenseType, typically on initial load or after save.
    if (isNgOnInit) {
      visibleOtherExpenses.sort((a, b) => a.expenseType - b.expenseType);
    }

    const needsPlaceholderExpense = visibleOtherExpenses?.length === 0;
    this.otherHousingExpenses.clear({ emitEvent: false });

    for (let i = 0; i < (visibleOtherExpenses?.length > 0 ? visibleOtherExpenses?.length : 1); i++) {
      const newFormGroup = createOtherExpenseFormGroup(this._fb);

      newFormGroup.patchValue(needsPlaceholderExpense ? createNewOtherExpense() : visibleOtherExpenses[i], { emitEvent: false });

      this.otherHousingExpenses.push(newFormGroup, { emitEvent: false });
    }

    this.rebuildExpenseDropdownOptions$.next();
  }

  /**
   * Disable or enable "Other Expense" proposed amount, depending on selected expense type.
   * @param otherExpenseControlNames Control name(s) for dynamic "Other Expense" row(s).
   */
  private _disableOrEnableOtherExpenses() {
    this.otherHousingExpenses.controls.forEach((ctrl) => {
      const formGroup = ctrl as FormGroup;
      const isReadOnly = formGroup.controls.expenseType.value === PrepaidItemType.NotAssigned;
      const controlToUpdate = formGroup.controls.proposedAmount;
      if (isReadOnly) {
        controlToUpdate.reset(undefined, { emitEvent: false });
        controlToUpdate.disable({ emitEvent: false });
      } else {
        controlToUpdate.enable({ emitEvent: false });
      }
    });
  }

  /** Initializes this.expensesForm with all controls + data, except for dynamic "Other Expenses" row(s). */
  private _initializeExpensesForm(expenses: CombinedHousingExpenseModel) {
    this.expensesForm.patchValue(expenses, { emitEvent: false });
    this.expensesForm.controls.originalHousingExpenses.setValue(expenses.otherHousingExpenses, { emitEvent: false });
    this.rebuildExpenseDropdownOptions$.next();
    // patching UI only control othersTotalPropertyInsurance.presentAmount.
    this.expensesForm.get('othersTotalPropertyInsurance.presentAmount').patchValue(expenses.othersTotal?.presentAmount, { emitEvent: false });
  }

  /**
   * Processes expenses data - recalculates values, updates expensesForm, and updates store as needed.
   * @param resetDirtyChecker Boolean whether to reset dirtyChecker for employment-income-expenses store. Default: `false`.
   */
  private _processExpensesData(resetDirtyChecker = false) {
    combineLatest([this.employmentIncomeExpense$, this._loanQuery.selectLoanInfo()])
      .pipe(
        filter(([empIncExp]) => !!empIncExp && !!empIncExp.applicationId),
        take(1),
        tap(([empIncExp]) => {
          this._updateTotals();
          this._disableOrEnableOtherExpenses();

          // https://dev.azure.com/cmgfidev/CLEAR%202/_workitems/edit/6248
          // if user entered proposed property taxes then we accept that
          // if the user changes factor, then we calculate the new taxes
          const proposedPropertyTaxes = this.expensesForm.get('realEstateTaxes.proposedAmount').value;

          // Ensures that 'otherHousingExpenses' FormArray includes latest values for "Other Expenses" added by user.
          const massagedData = {
            ...this.expensesForm.getRawValue(),
            realEstateTaxes: {
              ...this.expensesForm.get('realEstateTaxes').value,
              proposedAmount: proposedPropertyTaxes,
            },
            propertyTaxCalculationType: empIncExp.combinedHousingExpenses.propertyTaxCalculationType, // propertyTaxCalculationType input only exists on URLA, not LS
          };

          // Exclude invalid proposed amount to prevent custom error from being cleared
          if (this.expensesForm.get('realEstateTaxes.proposedAmount').invalid && massagedData?.realEstateTaxes) {
            delete massagedData.realEstateTaxes.proposedAmount;
          }

          this.expensesForm.patchValue(massagedData, { emitEvent: false });

          this._empIncExpStore.update(empIncExp.applicationId, {
            ...empIncExp,
            combinedHousingExpenses: massagedData,
          });

          // resetDirtyChecker: true - see _subscribeToLoanInfoChanges().
          if (resetDirtyChecker) {
            // This ensures the Reset/Save buttons aren't prematurely enabled.
            setTimeout(() => {
              this._empIncExpQuery.dirtyChecker2.dirtyChecker.setHead();
            }, 0);
          }
        }),
      )
      .subscribe();
  }

  /** Listen for updates to Loan Info, and update Expenses accordingly. */
  private _subscribeToLoanInfoChanges() {
    this._loanQuery
      .selectLoanInfo()
      .pipe(
        takeUntil(this._destroyed$),
        distinctUntilChanged((prev, curr) =>
          // Limit change detection to only these properties of Loan Info.
          [
            'interestRate',
            'isInterestOnly',
            'loanPurpose',
            'loanTerm',
            'monthlyMiAmount',
            'monthlyMiFactor',
            'relatedLoans',
            'salesPrice',
            'totalLoanAmount',
          ].every((prop) => prev[prop] === curr[prop]),
        ),
        tap((loan) => {
          // The calculated P & I of the base loan.
          const baseLoanMonthlyPi = calculatePI(loan.totalLoanAmount, loan.interestRate, loan.loanTerm, loan.isInterestOnly);

          // Summation of monthly payments for all Subordinate Financing loans.
          const subFiMonthlyPayments = loan.relatedLoans?.map((relLoan) => relLoan.monthlyPayment);
          const totalSubFiMonthly = subFiMonthlyPayments?.length > 0 ? subFiMonthlyPayments.reduce((a = 0, b = 0) => a + b) : null;

          if ([LoanPurposeType.notAssigned, LoanPurposeType.purchase, LoanPurposeType.refinance, LoanPurposeType.other].includes(loan.loanPurpose)) {
            // First Mortgage
            this.expensesForm.get('firstMortgage.proposedAmount')?.patchValue(baseLoanMonthlyPi, { emitEvent: false });

            // Subordinate Lien(s)
            this.expensesForm.get('otherMortgage.proposedAmount')?.patchValue(totalSubFiMonthly, { emitEvent: false });
          } else if (this._syncType === SyncType.SecondLien || this._syncType === SyncType.ThirdLien) {
            this.expensesForm
              .get('firstMortgage.proposedAmount')
              ?.patchValue(loan?.loanInfoCombinedHousingExpense?.firstMortgageProposed, { emitEvent: false });
            this.expensesForm.get('otherMortgage.proposedAmount')?.patchValue(loan?.subOrdinateFinance?.calculatedPi, { emitEvent: false });
          } else {
            // First Mortgage
            this.expensesForm.get('firstMortgage.proposedAmount')?.patchValue(null, { emitEvent: false });
            // Subordinate Lien(s)
            this.expensesForm.get('otherMortgage.proposedAmount')?.patchValue(baseLoanMonthlyPi + totalSubFiMonthly, { emitEvent: false });
          }

          let proposedPropertyTaxes = this.expensesForm.get('realEstateTaxes.proposedAmount').value;
          if (!proposedPropertyTaxes) {
            proposedPropertyTaxes = calculateProposedPropertyTax(loan.salesPrice, this.expensesForm.get('realEstateTaxes.factor').value);
          }
          // Property Taxes
          this.expensesForm.get('realEstateTaxes.proposedAmount')?.patchValue(proposedPropertyTaxes, {
            emitEvent: false,
          });

          // Mortgage Insurance
          this.expensesForm.get('mortgageInsurance.factor')?.patchValue(loan.monthlyMiFactor, { emitEvent: false });

          // Fallback to null when loan.monthlyMiAmount equals 0. This is a workaround to ensure Reset/Save remains disabled after save.
          // Front-end: On load, loan-mortgage-component changes monthlyMiAmount from null to 0 - see _calculateLoan().
          // Back-end: On save, API changes monthlyMiAmount from 0 (in request) to null (in response).
          this.expensesForm.get('mortgageInsurance.proposedAmount')?.patchValue(loan.monthlyMiAmount || null, { emitEvent: false });
        }),
        switchMap(() => {
          return this.employmentIncomeExpense$.pipe(
            take(1),
            tap(() => {
              // This ensures that during initial load, calculations in Loan AND Expenses do not mark store as dirty.
              const resetDirtyChecker = !this._empIncExpQuery.dirtyChecker2.isDirty();
              this._processExpensesData(resetDirtyChecker);
            }),
          );
        }),
      )
      .subscribe();

    this._loanStore.salesPriceUpdate$
      .pipe(
        takeUntil(this._destroyed$),
        tap((salesPrice) => {
          this._updatePropertyTaxes(PropertyTaxUpdateType.SalesPrice, salesPrice);
        }),
      )
      .subscribe();
  }

  private _updatePropertyTaxes(updateType: PropertyTaxUpdateType, value?: number): void {
    this._loanQuery
      .selectLoanInfo()
      .pipe(
        take(1),
        tap((loanInfo) => {
          const currentFactor = this.expensesForm.get('realEstateTaxes.factor').value;
          const currentAmount = this.expensesForm.get('realEstateTaxes.proposedAmount').value;
          const currentSalesPrice = loanInfo?.salesPrice;

          switch (updateType) {
            case PropertyTaxUpdateType.SalesPrice: {
              if (!currentFactor && currentAmount) {
                const factor = calculateProposedPropertyTaxFactor(value, currentAmount);
                if (factor > 100.0) {
                  this._messageService.add({
                    key: 'propertyTaxesWarning',
                    severity: 'warn',
                    summary: 'Warning!',
                    detail: `${(+Number(currentAmount).toFixed(2)).toLocaleString()}`,
                    styleClass: 'propTaxes',
                  });
                  this.expensesForm.get('realEstateTaxes.proposedAmount')?.patchValue(null, { emitEvent: false });
                } else {
                  this.expensesForm.get('realEstateTaxes.factor')?.patchValue(factor, { emitEvent: false });
                }
              }
              if (currentFactor) {
                const amount = calculateProposedPropertyTax(value, currentFactor);
                this.expensesForm.get('realEstateTaxes.proposedAmount')?.patchValue(amount, { emitEvent: false });
              }
              break;
            }
            case PropertyTaxUpdateType.Factor: {
              if (currentSalesPrice) {
                const amount = calculateProposedPropertyTax(currentSalesPrice, value);
                this.expensesForm.get('realEstateTaxes.proposedAmount')?.patchValue(amount, { emitEvent: false });
              }
              break;
            }
            case PropertyTaxUpdateType.Amount: {
              if (currentSalesPrice) {
                const factor = calculateProposedPropertyTaxFactor(currentSalesPrice, value);
                if (factor > 100.0) {
                  const maxAmount = calculateProposedPropertyTax(currentSalesPrice, 100);
                  this.expensesForm.get('realEstateTaxes.proposedAmount')?.patchValue(null, { emitEvent: false });
                  this.expensesForm.get('realEstateTaxes.proposedAmount')?.setErrors(
                    {
                      cmgValidationError: `Cannot exceed $${(+Number(maxAmount).toFixed(2)).toLocaleString()}`,
                    },
                    { emitEvent: false },
                  );
                } else {
                  this.expensesForm.get('realEstateTaxes.proposedAmount')?.setErrors(null, { emitEvent: false });
                  this.expensesForm.get('realEstateTaxes.factor')?.patchValue(factor, { emitEvent: false });
                }
              }
              break;
            }
            default:
              break;
          }

          this._processExpensesData();
        }),
      )
      .subscribe();
  }

  /** Updates the "Others Total" and "Total" in expensesForm and expenseRows$. */
  private _updateTotals() {
    const baseExpenses = ['firstMortgage', 'otherMortgage', 'hazardInsurance', 'realEstateTaxes', 'mortgageInsurance', 'homeOwnerAssnDues'];

    const otherHousingExpensesControls = this.otherHousingExpenses.controls;

    // Calculate and set value for "Others Total".
    this.expensesForm.get('othersTotal').patchValue(
      {
        proposedAmount: calculateSum(otherHousingExpensesControls.map((control) => control.value.proposedAmount)),
        presentAmount: this.expensesForm.get('othersTotalPropertyInsurance.presentAmount')?.value ?? null,
      },
      { emitEvent: false },
    );

    // Calculate and set value for "Total".
    this.expensesForm.get('total').patchValue(
      {
        presentAmount: calculateSum(
          ['presentRent', ...baseExpenses, 'othersTotalPropertyInsurance'].map(
            (control) => this.expensesForm.get(control + (control === 'presentRent' ? '' : '.presentAmount')).value,
          ),
        ),
        proposedAmount: calculateSum([
          ...baseExpenses.map((control) => this.expensesForm.get(control + '.proposedAmount').value),
          ...otherHousingExpensesControls.map((control) => control.value.proposedAmount),
        ]),
      },
      { emitEvent: false },
    );
  }

  private _subscribeToCalculatorModalFormChanges() {
    this.calculatorModalForm.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        tap((newValues: { monthlyPayment?: number; yearlyPayment?: number }) => {
          // Represents whether the "Monthly Payment" control was updated.
          const wasMonthlyUpdated = this.calculatorFormSnapshot.monthlyPayment !== newValues.monthlyPayment;

          this.calculatorModalForm.patchValue(
            wasMonthlyUpdated
              ? {
                  yearlyPayment: newValues.monthlyPayment !== null ? newValues.monthlyPayment * 12 : null,
                }
              : {
                  monthlyPayment: newValues.yearlyPayment !== null ? newValues.yearlyPayment / 12 : null,
                },
            { emitEvent: false },
          );

          // Update snapshot to use newly calculated values.
          this.calculatorFormSnapshot = this.calculatorModalForm.value;
        }),
      )
      .subscribe();
  }
}
